import React from "react";
import { Box, HStack, Text, Tooltip } from "@chakra-ui/react";
import { useVizTokens } from "./tokens";
import { WEEKDAYS, count, hourWords, money } from "./format";

// When texting costs: one cell an hour of each weekday (clinic time), darker where it cost more.
// A sequential scale, one hue; an empty hour is the surface's own gray. Each cell is the hover
// and focus target and says its texts and cost.
export default function Heatmap({ cells }) {
  const t = useVizTokens();
  const byKey = new Map((cells || []).map((c) => [`${c.day}:${c.hour}`, c]));
  const max = Math.max(0, ...(cells || []).map((c) => c.cost || 0));
  const steps = t.sequential;
  const colorOf = (cost) => {
    if (!cost || max <= 0) return t.empty;
    return steps[Math.min(steps.length - 1, Math.floor((cost / max) * steps.length))];
  };
  return (
    <Box overflowX="auto">
      <Box minW="680px" display="grid" gridTemplateColumns="40px repeat(24, minmax(18px, 1fr))" gap="2px"
           role="grid" aria-label="Texting cost by weekday and hour">
        <Box />
        {Array.from({ length: 24 }, (_, hour) => (
          <Text key={hour} fontSize="10px" color={t.muted} textAlign="center" role="columnheader">
            {hour % 3 === 0 ? hourWords(hour).replace(" ", "") : ""}
          </Text>
        ))}
        {WEEKDAYS.map((name, day) => (
          <React.Fragment key={name}>
            <Text fontSize="xs" color={t.textSecondary} alignSelf="center" role="rowheader">{name}</Text>
            {Array.from({ length: 24 }, (_, hour) => {
              const cell = byKey.get(`${day}:${hour}`);
              const words = `${name} ${hourWords(hour)}: ${cell ? `${count(cell.texts)} texts · ${money(cell.cost)}` : "nothing sent"}`;
              return (
                <Tooltip key={hour} label={words} hasArrow openDelay={80}>
                  <Box h="22px" borderRadius="3px" bg={colorOf(cell?.cost)} tabIndex={0} role="gridcell"
                       aria-label={words} _hover={{ outline: "2px solid", outlineColor: t.text, outlineOffset: "-1px" }}
                       _focusVisible={{ outline: "2px solid", outlineColor: t.text, outlineOffset: "-1px" }} />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      <HStack spacing={1.5} mt={3} fontSize="xs" color={t.muted}>
        <Text>Less</Text>
        {steps.map((c) => <Box key={c} w="16px" h="10px" borderRadius="2px" bg={c} />)}
        <Text>More (up to {money(max)} in an hour)</Text>
      </HStack>
    </Box>
  );
}
